import { FieldProps } from 'formik';
import React from 'react';
import { FormLabel } from 'react-bootstrap';
import AcknowledgmentField from '../../../controls/acknowledgment/AcknowledgmentField';
import BirthDateField from '../../../controls/birthdate/BirthDateField';
import CheckBoxField from '../../../controls/checkbox/CheckBoxField';
import DateTimeField from '../../../controls/datetime/DateTimeField';
import DateField from '../../../controls/date/DateField';
import LongTextField from '../../../controls/longtext/LongTextField';
import NumericField from '../../../controls/numeric/NumericField';
import OptionListField from '../../../controls/optionlist/OptionListField';
import RatingField from '../../../controls/rating/RatingField';
import SelectField from '../../../controls/select/SelectField';
import StaticTextField from '../../../../../library/inputs/staticText/StaticTextField';
import TextField from '../../../controls/text/TextField';
import TimeField from '../../../controls/time/TimeField';
import ToggleField from '../../../controls/toggle/ToggleField';
import WebLinkField from '../../../../../library/inputs/weblink/WebLinkField';
import YesNoField from '../../../controls/yesNoNa/YesNoField';
import HtmlField from '../../../../../library/inputs/html/HtmlField';
import SignatureField from '../../../controls/signature/SignatureField';
import DocumentViewerField from '../../../../../library/inputs/documentViewer/DocumentViewerField';
import ImageField from '../../../../../library/inputs/image/ImageField';
import VideoField from '../../../../../library/inputs/video/VideoField';
import { FieldType } from '../../../../../../models/form/fieldType';
import { FieldModel } from '../../../../../../models/form/field';
import UploadFileField from '../../../controls/uploadFile/UploadFileField';
import PaymentOptionListField from '../../../controls/payment/options/PaymentOptionListField';
import PaymentChoiceListField from '../../../controls/payment/choices/PaymentChoiceListField';
import { getText } from '../../../../../../utilities/CultureUtilities';
import ProductsField from '../../../controls/payment/products/ProductsField';

export function getFieldComponent(
  field: FieldModel,
  fieldProperties: any,
  fieldProps: FieldProps,
  culture: string,
  readOnlyMode?: boolean,
): React.ReactNode {
  switch (field.type) {
    case FieldType.Text:
      return (
        <TextField
          readOnlyMode={readOnlyMode}
          enabled={field.enabled}
          placeholder={getText(field.placeholder, culture)}
          id={fieldProps.field.name}
          handleBlur={fieldProps.field.onBlur}
          value={fieldProps.field.value}
          error={fieldProps.meta.error}
          touched={fieldProps.meta.touched}
          handleChange={fieldProps.field.onChange}
        />
      );
    case FieldType.Birthdate:
      return (
        <BirthDateField
          readOnlyMode={readOnlyMode}
          enabled={field.enabled}
          placeholder={getText(field.placeholder, culture)}
          id={fieldProps.field.name}
          setFieldTouched={fieldProps.form.setFieldTouched}
          value={fieldProps.field.value}
          error={fieldProps.meta.error}
          touched={fieldProps.meta.touched}
          setFieldValue={fieldProps.form.setFieldValue}
        />
      );
    case FieldType.Date:
      return (
        <DateField
          readOnlyMode={readOnlyMode}
          enabled={field.enabled}
          placeholder={getText(field.placeholder, culture)}
          id={fieldProps.field.name}
          setFieldTouched={fieldProps.form.setFieldTouched}
          value={fieldProps.field.value}
          error={fieldProps.meta.error}
          touched={fieldProps.meta.touched}
          setFieldValue={fieldProps.form.setFieldValue}
        />
      );
    case FieldType.Datetime:
      return (
        <DateTimeField
          readOnlyMode={readOnlyMode}
          enabled={field.enabled}
          placeholder={getText(field.placeholder, culture)}
          id={fieldProps.field.name}
          setFieldTouched={fieldProps.form.setFieldTouched}
          value={fieldProps.field.value}
          error={fieldProps.meta.error}
          touched={fieldProps.meta.touched}
          setFieldValue={fieldProps.form.setFieldValue}
        />
      );
    case FieldType.EmailAddress:
      return (
        <TextField
          readOnlyMode={readOnlyMode}
          enabled={field.enabled}
          placeholder={getText(field.placeholder, culture)}
          id={fieldProps.field.name}
          handleBlur={fieldProps.field.onBlur}
          value={fieldProps.field.value}
          error={fieldProps.meta.error}
          touched={fieldProps.meta.touched}
          handleChange={fieldProps.field.onChange}
        />
      );
    case FieldType.LongText:
      return (
        <LongTextField
          readOnlyMode={readOnlyMode}
          enabled={field.enabled}
          placeholder={getText(field.placeholder, culture)}
          id={fieldProps.field.name}
          handleBlur={fieldProps.field.onBlur}
          value={fieldProps.field.value}
          error={fieldProps.meta.error}
          touched={fieldProps.meta.touched}
          handleChange={fieldProps.field.onChange}
        />
      );
    case FieldType.Numeric:
      return (
        <NumericField
          readOnlyMode={readOnlyMode}
          enabled={field.enabled}
          placeholder={getText(field.placeholder, culture)}
          id={fieldProps.field.name}
          handleBlur={fieldProps.field.onBlur}
          value={fieldProps.field.value}
          error={fieldProps.meta.error}
          touched={fieldProps.meta.touched}
          handleChange={fieldProps.field.onChange}
        />
      );
    case FieldType.OptionList:
      return (
        <OptionListField
          readOnlyMode={readOnlyMode}
          id={fieldProps.field.name}
          setFieldValue={fieldProps.form.setFieldValue}
          value={fieldProps.field.value}
          setFieldTouched={fieldProps.form.setFieldTouched}
          options={field.options.map((option) => {
            return {
              text: <span>{getText(option.text, culture)}</span>,
              value: option.value,
            };
          })}
          multiSelection={field.multiSelection}
        />
      );
    case FieldType.Select:
      return (
        <SelectField
          readOnlyMode={readOnlyMode}
          id={fieldProps.field.name}
          handleBlur={fieldProps.field.onBlur}
          value={fieldProps.field.value}
          handleChange={fieldProps.field.onChange}
          options={field.options}
          placeholder={getText(field.placeholder, culture)}
          culture={culture}
        />
      );
    case FieldType.StaticText:
      return <StaticTextField value={getText(field.value, culture)} textColor={field.textColor} />;
    case FieldType.Time:
      return (
        <TimeField
          readOnlyMode={readOnlyMode}
          enabled={field.enabled}
          placeholder={getText(field.placeholder, culture)}
          id={fieldProps.field.name}
          setFieldTouched={fieldProps.form.setFieldTouched}
          value={fieldProps.field.value}
          error={fieldProps.meta.error}
          touched={fieldProps.meta.touched}
          setFieldValue={fieldProps.form.setFieldValue}
        />
      );
    case FieldType.CheckBox:
      return (
        <CheckBoxField
          readOnlyMode={readOnlyMode}
          label={<FormLabel>{getText(field.text, culture)}</FormLabel>}
          enabled={field.enabled}
          id={fieldProps.field.name}
          value={fieldProps.field.value}
          setFieldValue={fieldProps.form.setFieldValue}
          setFieldTouched={fieldProps.form.setFieldTouched}
        />
      );
    case FieldType.Toggle:
      return (
        <ToggleField
          readOnlyMode={readOnlyMode}
          label={<FormLabel>{getText(field.text, culture)}</FormLabel>}
          enabled={field.enabled}
          id={fieldProps.field.name}
          value={fieldProps.field.value}
          setFieldValue={fieldProps.form.setFieldValue}
          setFieldTouched={fieldProps.form.setFieldTouched}
        />
      );
    case FieldType.WebLink:
      return (
        <WebLinkField
          value={getText(field.defaultValue, culture)}
          enabled={field.enabled}
          target={field.target}
          text={getText(field.text, culture)}
          textColor={field.textColor}
        />
      );
    case FieldType.Rating:
      return (
        <RatingField
          readOnlyMode={readOnlyMode}
          id={fieldProps.field.name}
          setFieldValue={fieldProps.form.setFieldValue}
          value={fieldProps.field.value}
          setFieldTouched={fieldProps.form.setFieldTouched}
          count={field.count}
          allowHalf={field.allowHalf}
          color={field.starColor}
        />
      );
    case FieldType.YesNo:
      return (
        <YesNoField
          readOnlyMode={readOnlyMode}
          id={field.id}
          setFieldValue={fieldProps.form.setFieldValue}
          setFieldTouched={fieldProps.form.setFieldTouched}
          value={fieldProps.field.value}
          enabled={field.enabled}
          allowNa={field.allowNa}
        />
      );
    case FieldType.Acknowledgment:
      return (
        <AcknowledgmentField
          readOnlyMode={readOnlyMode}
          enabled={field.enabled}
          id={fieldProps.field.name}
          value={fieldProps.field.value}
          setFieldValue={fieldProps.form.setFieldValue}
          setFieldTouched={fieldProps.form.setFieldTouched}
        />
      );
    case FieldType.HTML:
      return <HtmlField value={getText(field.value, culture)} />;
    case FieldType.Signature:
      return (
        <SignatureField
          readOnlyMode={readOnlyMode}
          id={fieldProps.field.name}
          setFieldValue={fieldProps.form.setFieldValue}
          setFieldTouched={fieldProps.form.setFieldTouched}
          img={fieldProps.field.value}
        />
      );
    case FieldType.DocumentViewer:
      return (
        <DocumentViewerField
          value={getText(field.defaultValue, culture)}
          documentType={field.documentType}
          documentName={getText(field.documentName, culture)}
        />
      );
    case FieldType.EmbeddedPhoto:
      return <ImageField src={getText(field.defaultValue, culture)} alt={getText(field.alt, culture)} />;
    case FieldType.Video:
      return <VideoField src={getText(field.defaultValue, culture)} videoPlayer={field.videoPlayer} />;
    case FieldType.UploadFile:
      return (
        <UploadFileField
          readOnlyMode={readOnlyMode}
          id={field.id}
          setFieldValue={fieldProps.form.setFieldValue}
          setFieldTouched={fieldProps.form.setFieldTouched}
          value={fieldProps.field.value}
          enabled={field.enabled}
        />
      );
    case FieldType.PaymentOptionList:
      return (
        <PaymentOptionListField
          readOnlyMode={readOnlyMode || false}
          id={fieldProps.field.name}
          setFieldValue={fieldProps.form.setFieldValue}
          value={fieldProps.field.value}
          setFieldTouched={fieldProps.form.setFieldTouched}
          options={field.options}
          multiSelection={false}
          culture={culture}
        ></PaymentOptionListField>
      );
    case FieldType.PaymentChoiceList:
      return (
        <PaymentChoiceListField
          readOnlyMode={readOnlyMode || false}
          id={fieldProps.field.name}
          setFieldValue={fieldProps.form.setFieldValue}
          value={fieldProps.field.value}
          setFieldTouched={fieldProps.form.setFieldTouched}
          options={field.choices}
          multiSelection={true}
          culture={culture}
        ></PaymentChoiceListField>
      );
    case FieldType.Products:
      return (
        <ProductsField
          readOnlyMode={readOnlyMode || false}
          id={fieldProps.field.name}
          setFieldValue={fieldProps.form.setFieldValue}
          value={fieldProps.field.value}
          setFieldTouched={fieldProps.form.setFieldTouched}
          products={field.products}
          fieldProperties={fieldProperties}
          culture={culture}
          touched={fieldProps.meta.touched}
          errors={fieldProps.form.errors}
        ></ProductsField>
      );
  }
}
